import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ChevronDown, Crown, PartyPopper, Hourglass, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader } from "../components/kit";
import { BusinessSection, ServicesSection, AreaSection, DescriptionSection, LogoSection, GallerySection, DocsSection } from "./ProviderSections";
import { AdvertForm, BLANK } from "./Adverts";
import { useSetup, stepsFor, summarise, isInvited, ProgressBar } from "../components/SetupChecklist";

const WEEK = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];

function WorkingDays({ p, set }) {
  const wd = p.working_days || WEEK.map(([k]) => k);
  const toggle = (k) => set("working_days", wd.includes(k) ? wd.filter((x) => x !== k) : [...wd, k]);
  return <div className="flex flex-wrap gap-2">{WEEK.map(([k, l]) => <button key={k} type="button" onClick={() => toggle(k)} className={`w-14 rounded-xl border-2 py-2 text-sm font-semibold transition-colors ${wd.includes(k) ? "border-[#374B5C] bg-[#374B5C] text-white" : "border-slate-200 text-slate-500"}`} data-testid={`setup-day-${k}`}>{l}</button>)}</div>;
}

function Hero({ p, user, sm }) {
  const first = (user?.name || "").split(" ")[0];
  const invited = isInvited(p);
  return (
    <div className="mb-6">
      <h1 className="text-3xl sm:text-4xl font-bold text-slate-800" data-testid="setup-title">Welcome{first ? `, ${first}` : ""}! Let's get you live</h1>
      <p className="muted mt-2 text-sm sm:text-base">{invited && p.plan_expires
        ? <><Crown size={15} className="inline text-amber-500 -mt-0.5" /> Your {p.plan_details?.name || "Premium"} membership is free until <b>{fmtDate(p.plan_expires)}</b>. Finish the steps below and homeowners can start finding you.</>
        : "Finish the steps below to get your profile ready. Most people are done in about 10 minutes."}</p>
      <div className="mt-4 flex items-center gap-3"><div className="flex-1"><ProgressBar pct={sm.pct} /></div><span className="text-sm font-semibold text-[#374B5C]" data-testid="setup-count">{sm.done} of {sm.total} done</span></div>
    </div>
  );
}

function DoneBanner({ p }) {
  const invited = isInvited(p);
  const pending = p.status === "pending";
  return (
    <div className={`card p-5 mb-5 flex flex-col sm:flex-row sm:items-center gap-3 ${pending ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="setup-done-banner">
      {pending ? <Hourglass className="text-amber-600" /> : <PartyPopper className="text-emerald-600" />}
      <div className="flex-1 text-sm"><b>{pending ? "Submitted for approval." : invited || p.status === "approved" ? "Your profile is live!" : "All required steps are done."}</b> {pending ? "We'll email you as soon as you're approved." : "Homeowners can now find you in search. The optional steps help you stand out."}</div>
      {p.slug && <Link to={`/professionals/${p.slug}`} className="btn-ghost btn-sm" data-testid="setup-view-profile">View public profile</Link>}
      <Link to="/provider" className="btn-navy btn-sm" data-testid="setup-go-dashboard">Go to dashboard</Link>
    </div>
  );
}

function StepHead({ s, i, open, locked, onClick }) {
  return (
    <button type="button" onClick={onClick} disabled={locked} className="w-full flex items-center gap-3 p-4 text-left disabled:opacity-50" data-testid={`setup-step-${s.key}`}>
      {s.done ? <CheckCircle2 className="text-emerald-500 shrink-0" size={22} /> : locked ? <Lock className="text-slate-300 shrink-0" size={20} /> : <Circle className="text-slate-300 shrink-0" size={22} />}
      <div className="flex-1 min-w-0"><div className={`font-semibold text-sm sm:text-base ${s.done ? "text-slate-500" : "text-slate-800"}`}>{i + 1}. {s.title}</div>{!s.done && <div className="text-xs muted mt-0.5">{s.why}</div>}</div>
      <span className={`chip text-[10px] ${s.req ? "bg-[#FFF1EE] text-[#D9583D]" : "bg-slate-100 text-slate-500"}`}>{s.done ? "Done" : s.req ? "Required" : "Optional"}</span>
      {s.key !== "account" && <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />}
    </button>
  );
}

function StepBody({ k, p, set, onAdvert, onClose }) {
  const plan = p.plan_details;
  if (k === "advert") return <AdvertForm init={{ ...BLANK, category: p.categories?.[0] || "" }} onSaved={onAdvert} onCancel={onClose} />;
  return {
    business: <BusinessSection p={p} set={set} />, services: <ServicesSection p={p} set={set} />, area: <AreaSection p={p} set={set} plan={plan} />,
    about: <DescriptionSection p={p} set={set} />, logo: <LogoSection p={p} set={set} />, photos: <GallerySection p={p} set={set} plan={plan} />,
    days: <WorkingDays p={p} set={set} />, docs: <DocsSection p={p} set={set} types={["Insurance", "ID", "Qualification", "Accreditation", "Certificate", "Business"]} />,
    submit: <p className="text-sm muted">Everything looks good. Submit your profile and our team will review it. You can keep editing while you wait.</p>,
  }[k];
}

export default function ProWelcome() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const { p, setP, ads, reload } = useSetup();
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const steps = p ? stepsFor(p, ads) : [];
  const sm = summarise(steps);
  useEffect(() => { if (p && open === null) setOpen(sm.next?.key || ""); }, [p]); // eslint-disable-line
  if (!p) return <Loader />;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const openNext = (after) => setOpen(stepsFor(after.p, after.ads).find((s) => !s.done && s.req)?.key || stepsFor(after.p, after.ads).find((s) => !s.done)?.key || "");
  const save = async (key) => {
    if (key === "about" && (p.description || "").trim().length < 50) return toast.error("Please write at least 50 characters about your business");
    setBusy(true);
    try {
      const extra = key === "days" ? { working_days: p.working_days || WEEK.map(([d]) => d) } : key === "submit" ? { submit: true } : {};
      const { data } = await api.put("/provider/me", { ...p, ...extra });
      const next = { ...p, ...data };
      setP(next); await refresh();
      toast.success(key === "submit" ? "Submitted for approval" : "Saved");
      openNext({ p: next, ads });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const onAdvert = async () => { await reload(); toast.success("Your first advert is live on your profile"); setOpen(""); };
  return (
    <div className="max-w-3xl" data-testid="setup-page">
      <Hero p={p} user={user} sm={sm} />
      {sm.reqLeft === 0 && <DoneBanner p={p} />}
      <div className="card divide-y divide-slate-100">{steps.map((s, i) => {
        const locked = !p.id && !["account", "business"].includes(s.key);
        const isOpen = open === s.key && s.key !== "account";
        return (
          <div key={s.key} className={isOpen ? "bg-[#FFFCFA]" : ""}>
            <StepHead s={s} i={i} open={isOpen} locked={locked} onClick={() => setOpen(isOpen ? "" : s.key)} />
            {isOpen && <div className="px-4 pb-5 sm:pl-12 fade-up" data-testid={`setup-body-${s.key}`}>
              <StepBody k={s.key} p={p} set={set} onAdvert={onAdvert} onClose={() => setOpen("")} />
              {s.key !== "advert" && <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => save(s.key)} disabled={busy || (s.key === "business" && !p.business_name)} className="btn-coral" data-testid={`setup-save-${s.key}`}>{busy ? "Saving…" : s.key === "submit" ? "Submit for approval" : "Save & continue"}</button></div>}
            </div>}
          </div>
        );
      })}</div>
      <div className="text-center mt-6"><button onClick={() => nav("/provider")} className="text-sm muted underline" data-testid="setup-later-btn">I'll finish this later</button></div>
    </div>
  );
}
